import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { endSessionLocally } from '@/features/auth/refresh'
import { useSessionStore } from '@/features/auth/session-store'
import { acquireRealtime } from './singleton'

/**
 * Holds the websocket open for as long as the calling component is mounted.
 *
 * Mount it once, high in the tree. Several call sites are safe -- the connection
 * is ref-counted -- but there is no reason for more than one.
 */
export function useRealtimeConnection(options: { anonymous?: boolean } = {}): void {
  const queryClient = useQueryClient()
  const authenticated = useSessionStore((state) => state.authenticated)
  const anonymous = options.anonymous ?? false

  useEffect(() => {
    // A socket opened without a token is refused and then retried until the
    // attempt budget runs out, which puts a "connection failed" banner in front
    // of somebody who is simply not signed in yet.
    if (!authenticated && !anonymous) return

    return acquireRealtime(
      // The involuntary path: a revoked session ends here, not at the identity
      // provider, whose session belongs to every other Lithos service too.
      { queryClient, endSession: endSessionLocally },
      { anonymous },
    )
  }, [authenticated, anonymous, queryClient])
}
